import _defineProperty from "/app/node_modules/.pnpm/next@13.4.17_@babel+core@7.14.3_@opentelemetry+api@1.4.1_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import { Component } from 'react';
import { connect } from 'react-redux';
import { uniqBy } from 'lodash';
import { getUserItems, getUserById, getUserItemFacets, getFeaturedCollections, getProfileUserInfo, getCatalogs, exposeCollections } from 'data/api';
import { transformCatalogDtosWithCounts } from 'data/transformers/catalog';
import { transformFeaturedCollection } from 'data/transformers/featured-collection';
import { AbTestVariant, DEFAULT_PAGE_SIZE } from 'constants/index';
import { actions as itemActions } from 'state/items/slice';
import { SortByOption } from 'constants/filter';
import { ItemAlertStatus } from 'constants/item';
import { mergeItemsWithDrafts } from 'data/utils/item';
import { logMessage } from 'libs/utils/window';
import { getAbTestByName } from 'state/ab-tests/selectors';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const mapActionsToProps = {
  setUserItems: itemActions.setUserItems
};

const mapStateToProps = state => {
  const isPortalMergeSourceEnabled = state.featureSwitches.portal_merge_source || false;
  const multipleCollectionsAbTest = getAbTestByName('multiple_collections')(state);
  return {
    isPortalMergeSourceEnabled,
    multipleCollectionsAbTest
  };
};

const connector = connect(mapStateToProps, mapActionsToProps);

const filterDraftItems = items => items.filter(item => item.is_draft);

const filterReplicaItems = items => items.filter(item => {
  var _item$item_alert;

  return ((_item$item_alert = item.item_alert) === null || _item$item_alert === void 0 ? void 0 : _item$item_alert.item_alert_type) === ItemAlertStatus.ReplicaProof;
});

const STARTING_PAGE_INDEX = 0;

class ProfileFetcher extends Component {
  constructor() {
    super(...arguments);

    _defineProperty(this, "state", {
      currentPage: STARTING_PAGE_INDEX,
      endReached: false,
      isLoading: false,
      items: [],
      itemsCount: 0,
      totalItemsCount: 0,
      catalogs: [],
      collections: [],
      user: null,
      filters: {
        sortBy: SortByOption.Relevance,
        catalogId: null
      },
      currentFilter: undefined,
      userInfo: {
        showSupportFunctionality: false,
        canSendTemplates: false,
        feedbackProps: {
          canAddFeedback: false,
          leaveFeedbackUrl: undefined
        }
      },
      showActions: true
    });

    _defineProperty(this, "draftItems", []);

    _defineProperty(this, "replicaItems", []);

    _defineProperty(this, "fetchUser", async () => {
      const response = await getUserById({
        id: this.props.userId
      });
      if ('errors' in response) return;
      this.setState({
        user: response.user
      });
      this.setShowActions();
    });

    _defineProperty(this, "fetchUserInfo", async () => {
      const response = await getProfileUserInfo(this.props.userId);
      if ('errors' in response) return;
      this.setState({
        userInfo: {
          showSupportFunctionality: response.show_support_functionality,
          canSendTemplates: response.can_send_templates,
          feedbackProps: {
            canAddFeedback: response.can_add_feedback,
            leaveFeedbackUrl: typeof response.url_for_last_unfeedbacked_transaction === 'string' ? response.url_for_last_unfeedbacked_transaction : undefined
          },
          isShadowBanned: response === null || response === void 0 ? void 0 : response.shadow_banned
        }
      });
    });

    _defineProperty(this, "exposeCollections", async () => {
      const {
        userId
      } = this.props;
      await exposeCollections(userId);
    });

    _defineProperty(this, "fetchCollections", async () => {
      const {
        userId
      } = this.props;
      const response = await getFeaturedCollections({
        userId
      });
      if ('errors' in response) return;
      const collections = response.featured_collections.map(transformFeaturedCollection);
      this.setState({
        collections
      });
    });

    _defineProperty(this, "resolveTotalItemsCount", _ref => {
      let {
        isAllItemsFilterSelected,
        isCatalogSelected,
        itemCount
      } = _ref;
      const {
        totalItemsCount
      } = this.state;
      if (isCatalogSelected || !isAllItemsFilterSelected) return totalItemsCount;
      return itemCount;
    });

    _defineProperty(this, "fetchItems", async selectedFilter => {
      const {
        userId,
        pageSize
      } = this.props;
      const {
        currentPage,
        endReached,
        filters,
        isLoading,
        currentFilter
      } = this.state;
      const hasFilterChanged = currentFilter !== selectedFilter;
      const isAllItemsFilterSelected = !selectedFilter;
      if (!hasFilterChanged && (endReached || isLoading)) return;

      if (hasFilterChanged) {
        this.setState({
          items: []
        });
        this.replicaItems = [];
      }

      this.setState({
        isLoading: true,
        currentFilter: selectedFilter
      });
      const response = await getUserItems({
        userId,
        currentPage: hasFilterChanged ? STARTING_PAGE_INDEX : currentPage,
        perPage: pageSize,
        order: filters.sortBy,
        catalogIds: filters.catalogId ? [filters.catalogId] : undefined,
        filter: selectedFilter
      }); // This may happen when user changed filter before the first request has finished

      if (this.state.currentFilter !== selectedFilter) return;

      if ('errors' in response) {
        logMessage("Items fetching error, status code: ".concat(response.status || 'null', ", message: ").concat(response.message), {
          feature: 'closet_filtering',
          extra: selectedFilter || 'all'
        });
      }

      if ('errors' in response || !response.items && !response.drafts) {
        this.setState({
          isLoading: false
        });
        return;
      }

      const {
        current_page,
        total_pages,
        total_entries
      } = response.pagination;
      const totalItemsCount = this.resolveTotalItemsCount({
        isAllItemsFilterSelected,
        isCatalogSelected: !!filters.catalogId,
        itemCount: total_entries
      });

      if (totalItemsCount === 0 && hasFilterChanged && isAllItemsFilterSelected) {
        logMessage('Total item count decreased to zero', {
          feature: 'closet_filtering'
        });
      }

      const items = mergeItemsWithDrafts(response);
      this.draftItems = uniqBy([...this.draftItems, ...filterDraftItems(response.items)], 'id');
      this.replicaItems = uniqBy([...this.replicaItems, ...filterReplicaItems(response.items)], 'id');
      this.setState(prevState => ({
        currentPage: current_page,
        endReached: current_page === total_pages,
        itemsCount: total_entries,
        totalItemsCount,
        isLoading: false,
        items: uniqBy([...this.replicaItems, ...prevState.items, ...items], 'id')
      }));
    });

    _defineProperty(this, "fetchUserItemFacets", async () => {
      const {
        userId
      } = this.props;
      const [catalogsResponse, userItemFacetsResponse] = await Promise.all([getCatalogs(), getUserItemFacets({
        userId
      })]);

      if ('errors' in catalogsResponse || 'errors' in userItemFacetsResponse) {
        this.setState({
          catalogs: []
        });
        return;
      }

      const availableCatalogs = transformCatalogDtosWithCounts(catalogsResponse.catalogs, userItemFacetsResponse.catalogs);
      this.setState({
        catalogs: availableCatalogs
      });
    });

    _defineProperty(this, "updateItems", items => {
      this.setState({
        items: uniqBy([...this.replicaItems, ...this.draftItems, ...items], 'id')
      });
    });

    _defineProperty(this, "updateFilters", async filters => {
      const {
        userId,
        pageSize
      } = this.props;
      this.setState({
        isLoading: true,
        filters
      });
      const response = await getUserItems({
        userId,
        currentPage: STARTING_PAGE_INDEX,
        perPage: pageSize,
        order: filters.sortBy,
        catalogIds: filters.catalogId === null ? null : [filters.catalogId]
      });

      if ('errors' in response || !response.items && !response.drafts) {
        this.setState({
          isLoading: false
        });
        return;
      }

      const {
        current_page,
        total_pages,
        total_entries
      } = response.pagination;
      const items = mergeItemsWithDrafts(response);
      this.setState({
        currentPage: current_page,
        endReached: current_page === total_pages,
        itemsCount: total_entries,
        isLoading: false,
        items
      });
    });

    _defineProperty(this, "setShowActions", () => {
      if (!this.state.user) return;
      const showActions = !this.props.isPortalMergeSourceEnabled && !(this.state.user.is_hated || this.state.user.hates_you) && this.state.user.allow_direct_messaging;
      this.setState(() => ({
        showActions
      }));
    });
  }

  componentDidMount() {
    const {
      multipleCollectionsAbTest
    } = this.props;
    const isMultipleCollectionsEnabled = !!(multipleCollectionsAbTest !== null && multipleCollectionsAbTest !== void 0 && multipleCollectionsAbTest.variant) && (multipleCollectionsAbTest === null || multipleCollectionsAbTest === void 0 ? void 0 : multipleCollectionsAbTest.variant) !== AbTestVariant.Off;
    this.fetchItems();
    this.fetchUser();
    this.fetchUserItemFacets();
    this.fetchUserInfo();

    if (!isMultipleCollectionsEnabled) {
      this.fetchCollections();
    }

    if (multipleCollectionsAbTest !== null && multipleCollectionsAbTest !== void 0 && multipleCollectionsAbTest.variant) {
      this.exposeCollections();
    }
  }

  componentDidUpdate() {
    const {
      items
    } = this.state;
    this.props.setUserItems({
      items
    });
  }

  render() {
    const {
      children
    } = this.props;
    const {
      user,
      items,
      catalogs,
      collections,
      isLoading,
      itemsCount,
      totalItemsCount,
      filters,
      userInfo,
      showActions
    } = this.state;
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children({
        user,
        userInfo,
        showActions,
        items,
        catalogs,
        collections,
        isLoading,
        itemsCount,
        totalItemsCount,
        filters,
        fetchMoreItems: this.fetchItems,
        updateItems: this.updateItems,
        updateFilters: this.updateFilters
      })
    });
  }

}

_defineProperty(ProfileFetcher, "defaultProps", {
  pageSize: DEFAULT_PAGE_SIZE
});

export default connector(ProfileFetcher);